// Money popups (FINANCE-001, owner 2026-10-22: "make forms in popup … according to the website and
// compact"), laid out like the Create order popup: fields on the left, a sunken summary column with
// the buttons on the right.
//   * MoneyMovePopup: Money in, Money out, Transfer, Add expense, Supplier bill, Pay supplier. Each is
//     one balanced journal entry (sensitive: step-up); entries never change, a mistake is reversed.
//   * AccountFormPopup: add or edit a money account, expense category, income category or supplier.
//   * EntryPopup: one journal entry with its lines and Reverse.
//   * AccountPopup: one account's balance and history, with the actions that fit it.
import { useMemo, useState, type FormEvent, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDate, formatDateTime, formatMoney, formatPhone } from "@ady/format";
import { t } from "@ady/i18n";
import {
  Banner,
  Button,
  cx,
  Icon,
  MoneyInput,
  Popup,
  SkeletonText,
  useToast,
  type IconName,
} from "@ady/ui";
import { ApiError } from "../api/client";
import { errorMessage } from "../api/errors";
import { stepUpErrorMessage, useStepUp } from "../auth/StepUp";
import { IconInput, Section } from "../orders/NewOrderDialog";
import { Money, useCan, useStoreIds } from "../orders/ui";
import {
  financeApi,
  financeKeys,
  nptDay,
  type AccountBalance,
  type AccountDetails,
  type JournalEntry,
} from "./api";
import {
  ENTRY_KIND_ICON,
  ENTRY_KIND_TONE,
  KIND_ICON,
  entryKind,
  entryTotal,
  isActive,
  isExpense,
  isHandMoney,
  isIncome,
  isMoney,
  isOwner,
  isSupplier,
  line,
  nameOf,
  newCode,
  useAccounts,
  type EntryKind,
} from "./money";

const frame =
  "min-h-control w-full rounded-xs border border-control bg-surface px-2.5 text-sm outline-none placeholder:text-fg-tertiary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus";
const keyOf = (a: AccountBalance) => a.id ?? a.code;

export function PopupHead({
  icon,
  title,
  onClose,
}: {
  icon: IconName;
  title: string;
  onClose: () => void;
}) {
  return (
    <header className="flex items-center gap-2 border-b border-subtle pb-2.5">
      <span className="flex size-7 items-center justify-center rounded-xs ady-bg-brand text-on-primary">
        <Icon name={icon} />
      </span>
      <h2 className="flex-1 text-start text-base font-semibold">{title}</h2>
      <button
        type="button"
        onClick={onClose}
        className="inline-flex size-7 items-center justify-center rounded-xs text-fg-secondary hover:bg-hover"
      >
        <Icon name="x" />
        <span className="ady-sr-only">{t("payments.money.close")}</span>
      </button>
    </header>
  );
}

/** The sunken right column: a summary card, an optional hint and the stacked buttons. */
export function Side({
  children,
  hint,
  onClose,
  submit,
}: {
  children: ReactNode;
  hint?: ReactNode;
  onClose: () => void;
  submit: {
    label: string;
    icon?: IconName;
    ready: boolean;
    why?: string;
    loading: boolean;
    danger?: boolean;
  };
}) {
  return (
    <div className="flex flex-col gap-2.5 rounded-sm border border-card bg-sunken p-3">
      {children}
      {hint ? <p className="text-2xs text-fg-tertiary">{hint}</p> : null}
      <div className="mt-auto flex flex-col-reverse gap-1.5 pt-1">
        <Button variant="secondary" fullWidth onClick={onClose}>
          {t("payments.money.cancel")}
        </Button>
        <Button
          type="submit"
          variant={submit.danger ? "danger" : "primary"}
          icon={submit.icon ?? "check"}
          fullWidth
          // Not `availability`: its tooltip would stick out past the popup's right edge.
          disabled={!submit.ready}
          title={submit.ready ? undefined : submit.why}
          className="disabled:cursor-not-allowed disabled:opacity-45"
          loading={submit.loading}
        >
          {submit.label}
        </Button>
      </div>
    </div>
  );
}

/** One account as a tappable card (icon, name, balance). */
function AccountChoice({
  a,
  on,
  onPick,
  disabled,
}: {
  a: AccountBalance;
  on: boolean;
  onPick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      disabled={disabled}
      onClick={onPick}
      className={cx(
        "flex min-h-control min-w-0 items-center gap-2 rounded-xs border-[1.5px] px-2 py-1 text-start disabled:cursor-not-allowed disabled:opacity-40",
        on ? "ady-outline-brand" : "border-card bg-surface hover:bg-hover",
      )}
    >
      <span className={on ? "text-link" : "text-fg-tertiary"}>
        <Icon name={KIND_ICON[a.kind] ?? "money"} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span
          className={cx("truncate text-xs", on ? "font-semibold text-link" : "font-medium")}
          data-content="user"
        >
          {nameOf(a)}
        </span>
        {isMoney(a) || isSupplier(a) ? (
          <span className="ady-ledger truncate text-2xs text-fg-tertiary">
            {formatMoney(a.balance_paisa)}
          </span>
        ) : null}
      </span>
      {on ? (
        <span className="text-link">
          <Icon name="check" />
        </span>
      ) : null}
    </button>
  );
}

/** Account cards plus "New …" (an inline name field that adds the account and picks it). */
function Picker({
  options,
  value,
  onChange,
  exclude,
  create,
  empty,
}: {
  options: AccountBalance[];
  value: string;
  onChange: (key: string) => void;
  exclude?: string;
  create?: { kind: "expense" | "revenue" | "payable"; label: string };
  empty?: string;
}) {
  const { orgId, storeId } = useStoreIds();
  const canPost = useCan("finance.post");
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [key] = useState(() => crypto.randomUUID());
  const add = useMutation({
    mutationFn: () =>
      financeApi.addAccount(
        orgId,
        storeId,
        {
          code: newCode(
            create!.kind === "expense" ? "exp" : create!.kind === "revenue" ? "inc" : "sup",
            name,
          ),
          name: name.trim(),
          kind: create!.kind,
        },
        `${key}-${name.trim()}`,
      ),
    onSuccess: async (a) => {
      await qc.invalidateQueries({ queryKey: financeKeys.accounts(orgId, storeId) });
      if (a?.id) onChange(a.id);
      setName("");
      setAdding(false);
    },
  });
  return (
    <div className="flex flex-col gap-1.5">
      {options.length === 0 && empty ? <p className="text-2xs text-fg-tertiary">{empty}</p> : null}
      <div className="grid gap-1.5 sm:grid-cols-2">
        {options.map((a) => (
          <AccountChoice
            key={keyOf(a)}
            a={a}
            on={value === keyOf(a)}
            disabled={exclude === keyOf(a)}
            onPick={() => onChange(keyOf(a))}
          />
        ))}
        {create && canPost && !adding ? (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="flex min-h-control items-center gap-1.5 rounded-xs border-[1.5px] border-dashed border-card px-2 text-xs font-medium text-link hover:bg-hover"
          >
            <Icon name="plus" />
            {create.label}
          </button>
        ) : null}
      </div>
      {create && adding ? (
        <div className="flex items-center gap-1.5">
          <input
            autoFocus
            value={name}
            maxLength={80}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (name.trim()) add.mutate();
              }
              if (e.key === "Escape") {
                e.stopPropagation();
                setAdding(false);
              }
            }}
            placeholder={t("payments.money.newNamePh")}
            aria-label={create.label}
            className={frame}
            data-content="user"
          />
          <Button
            size="sm"
            icon="check"
            loading={add.isPending}
            disabled={!name.trim()}
            onClick={() => add.mutate()}
          >
            {t("payments.money.addShort")}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>
            {t("payments.money.cancel")}
          </Button>
        </div>
      ) : null}
      {add.isError ? (
        <p className="text-2xs text-danger-fg" role="alert">
          {errorMessage(add.error)}
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- money move

export type MoveMode = "in" | "out" | "transfer" | "expense" | "bill" | "pay";
export interface MovePreset {
  /** A money account (key = id, or code for an unseeded chart account). */
  money?: string;
  /** The other side: a category, supplier or (transfer) the destination. */
  other?: string;
}

const MODE_ICON: Record<MoveMode, IconName> = {
  in: "download",
  out: "upload",
  transfer: "swap",
  expense: "receipt",
  bill: "file",
  pay: "users",
};

export function MoneyMovePopup({
  mode,
  preset,
  onClose,
}: {
  mode: MoveMode | null;
  preset?: MovePreset;
  onClose: () => void;
}) {
  return (
    <Popup
      open={mode !== null}
      onClose={onClose}
      size="md"
      label={mode ? t(`payments.money.move.${mode}.title`) : ""}
    >
      {mode ? (
        <MoveForm
          key={`${mode}-${preset?.money ?? ""}-${preset?.other ?? ""}`}
          mode={mode}
          preset={preset}
          onClose={onClose}
        />
      ) : null}
    </Popup>
  );
}

function MoveForm({
  mode,
  preset,
  onClose,
}: {
  mode: MoveMode;
  preset?: MovePreset;
  onClose: () => void;
}) {
  const { orgId, storeId } = useStoreIds();
  const canPost = useCan("finance.post");
  const qc = useQueryClient();
  const toast = useToast();
  const stepUp = useStepUp();
  const accounts = useAccounts();
  const all = (accounts.data?.data ?? []).filter(isActive);
  const today = nptDay(0);
  const [amount, setAmount] = useState<number | null>(null);
  const [date, setDate] = useState(today);
  const [money, setMoney] = useState(preset?.money ?? "");
  const [other, setOther] = useState(
    mode === "bill" || mode === "pay" ? "" : (preset?.other ?? ""),
  );
  const [supplier, setSupplier] = useState(
    mode === "bill" || mode === "pay" ? (preset?.other ?? "") : "",
  );
  // Add expense: paid now from a money account, or on credit (a bill from a supplier).
  const [onCredit, setOnCredit] = useState(false);
  const [ref, setRef] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string>();
  const [key] = useState(() => crypto.randomUUID());

  const handMoney = all.filter(isHandMoney);
  const anyMoney = all.filter((a) => isMoney(a) && a.kind !== "courier_receivable");
  // Built-in expense accounts (gateway fees, COD shortfalls, drawer over/short) are posted by the
  // system, so the pickers offer only the categories the merchant made.
  const expenses = all.filter((a) => isExpense(a) && !a.system);
  const suppliers = all.filter(isSupplier);
  const incomes = all.filter((a) => isIncome(a) || isOwner(a));
  const outs = all.filter((a) => (isExpense(a) && !a.system) || isOwner(a));
  const find = (k: string) => all.find((a) => keyOf(a) === k);

  // debit / credit sides of the entry, per mode
  const credit = mode === "bill" || (mode === "expense" && onCredit);
  const plan: { debit?: AccountBalance; credit?: AccountBalance } =
    mode === "in"
      ? { debit: find(money), credit: find(other) }
      : mode === "out" || (mode === "expense" && !onCredit)
        ? { debit: find(other), credit: find(money) }
        : credit
          ? { debit: find(other), credit: find(supplier) }
          : mode === "pay"
            ? { debit: find(supplier), credit: find(money) }
            : { debit: find(other), credit: find(money) }; // transfer: money → other
  const label = (a?: AccountBalance) => (a ? nameOf(a) : "");
  const memo = (() => {
    if (note.trim())
      return mode === "bill" && ref.trim()
        ? `${t("payments.money.billRef", { ref: ref.trim() })}: ${note.trim()}`
        : note.trim();
    switch (mode) {
      case "in":
        return t("payments.money.memo.in", { what: label(plan.credit) });
      case "out":
        return t("payments.money.memo.out", { what: label(plan.debit) });
      case "expense":
        return onCredit
          ? t("payments.money.memo.bill", { supplier: label(plan.credit), what: label(plan.debit) })
          : label(plan.debit);
      case "bill":
        return ref.trim()
          ? `${t("payments.money.billRef", { ref: ref.trim() })}: ${label(plan.debit)}`
          : t("payments.money.memo.bill", {
              supplier: label(plan.credit),
              what: label(plan.debit),
            });
      case "pay":
        return t("payments.money.memo.pay", { supplier: label(plan.debit) });
      case "transfer":
        return t("payments.money.memo.transfer", {
          from: label(plan.credit),
          to: label(plan.debit),
        });
    }
  })();

  const missing = !amount
    ? t("payments.money.need.amount")
    : !plan.debit || !plan.credit
      ? t(`payments.money.need.${mode}`)
      : keyOf(plan.debit) === keyOf(plan.credit)
        ? t("payments.finance.sameAccount")
        : date > today
          ? t("payments.money.need.date")
          : !canPost
            ? t("errors.forbidden")
            : undefined;
  const post = useMutation({
    mutationFn: () =>
      stepUp(() =>
        financeApi.post(
          orgId,
          storeId,
          {
            memo: memo.slice(0, 300),
            ...(date !== today ? { occurred_at: `${date}T12:00:00+05:45` } : {}),
            lines: [line(plan.debit!, "debit", amount!), line(plan.credit!, "credit", amount!)],
          },
          key,
        ),
      ),
    onSuccess: async () => {
      toast.show({
        tone: "success",
        title: t(`payments.money.move.${mode}.done`, { amount: formatMoney(amount!) }),
      });
      await qc.invalidateQueries({ queryKey: financeKeys.all(orgId, storeId) });
      onClose();
    },
    onError: (e) => setError(stepUpErrorMessage(e) ?? errorMessage(e)),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(undefined);
    if (!missing) post.mutate();
  };

  /** Balance before → after for a money account or supplier this entry touches. */
  const after = (a: AccountBalance | undefined, side: "debit" | "credit") => {
    if (!a || !(isMoney(a) || isSupplier(a))) return null;
    const debitNormal = isMoney(a);
    const delta = (amount ?? 0) * (side === "debit" ? 1 : -1) * (debitNormal ? 1 : -1);
    const next = a.balance_paisa + delta;
    return (
      <li className="flex flex-col gap-0.5 rounded-xs border border-card bg-surface px-2 py-1.5">
        <span className="flex items-center gap-1.5 text-2xs font-semibold text-fg-secondary">
          <Icon name={KIND_ICON[a.kind] ?? "money"} />
          <span className="truncate" data-content="user">
            {nameOf(a)}
          </span>
        </span>
        <span className="ady-ledger flex items-center gap-1 text-2xs text-fg-tertiary">
          {formatMoney(a.balance_paisa)}
          <Icon name="arrow" />
          <span
            className={cx("font-semibold", next < 0 && isMoney(a) ? "text-danger-fg" : "text-fg")}
          >
            {formatMoney(next)}
          </span>
        </span>
        {isSupplier(a) ? (
          <span className="text-2xs text-fg-tertiary">{t("payments.money.youOwe")}</span>
        ) : null}
      </li>
    );
  };
  const negative =
    amount !== null &&
    plan.credit !== undefined &&
    isMoney(plan.credit) &&
    plan.credit.balance_paisa - amount < 0;

  return (
    <form onSubmit={submit} className="flex flex-col">
      <PopupHead
        icon={MODE_ICON[mode]}
        title={t(`payments.money.move.${mode}.title`)}
        onClose={onClose}
      />
      {accounts.isPending ? (
        <div className="pt-3">
          <SkeletonText lines={5} />
        </div>
      ) : (
        <div className="grid gap-3 pt-3 text-start sm:grid-cols-[minmax(0,1fr)_13rem]">
          <div className="flex min-w-0 flex-col gap-2.5">
            {error ? <Banner kind="error" title={error} /> : null}
            <Section icon="coins" title={t("payments.money.amountDate")}>
              <div className="grid gap-1.5 sm:grid-cols-[minmax(0,1fr)_9.5rem]">
                <MoneyInput
                  value={amount}
                  onValueChange={(v) => setAmount(v)}
                  aria-label={t("payments.money.amount")}
                  autoFocus
                />
                <input
                  type="date"
                  value={date}
                  max={today}
                  onChange={(e) => setDate(e.target.value || today)}
                  aria-label={t("payments.money.date")}
                  className={frame}
                />
              </div>
            </Section>

            {mode === "transfer" ? (
              <>
                <Section icon="upload" title={t("payments.money.fromAccount")}>
                  <Picker options={anyMoney} value={money} onChange={setMoney} exclude={other} />
                </Section>
                <Section icon="download" title={t("payments.money.toAccount")}>
                  <Picker options={anyMoney} value={other} onChange={setOther} exclude={money} />
                </Section>
              </>
            ) : null}

            {mode === "bill" || mode === "pay" ? (
              <Section icon="users" title={t("payments.money.supplier")}>
                <Picker
                  options={suppliers}
                  value={supplier}
                  onChange={setSupplier}
                  empty={t("payments.money.noSuppliers")}
                  create={{ kind: "payable", label: t("payments.money.newSupplier") }}
                />
              </Section>
            ) : null}

            {mode === "in" ? (
              <Section icon="download" title={t("payments.money.intoAccount")}>
                <Picker options={handMoney} value={money} onChange={setMoney} />
              </Section>
            ) : null}

            {mode === "in" || mode === "out" || mode === "expense" || mode === "bill" ? (
              <Section
                icon={mode === "in" ? "trending" : "receipt"}
                title={
                  mode === "in"
                    ? t("payments.money.fromWhere")
                    : mode === "out"
                      ? t("payments.money.whatFor")
                      : t("payments.money.category")
                }
              >
                <Picker
                  options={mode === "in" ? incomes : mode === "out" ? outs : expenses}
                  value={other}
                  onChange={setOther}
                  create={
                    mode === "in"
                      ? { kind: "revenue", label: t("payments.money.newIncome") }
                      : { kind: "expense", label: t("payments.money.newCategory") }
                  }
                />
              </Section>
            ) : null}

            {mode === "expense" ? (
              <Section icon="wallet" title={t("payments.money.howPaid")}>
                <div
                  className="flex flex-wrap gap-1 rounded-sm border border-card bg-surface p-1"
                  role="group"
                >
                  {([false, true] as const).map((c) => (
                    <button
                      key={String(c)}
                      type="button"
                      aria-pressed={onCredit === c}
                      onClick={() => setOnCredit(c)}
                      className={cx(
                        "flex min-h-7 flex-1 items-center justify-center gap-1.5 rounded-xs px-2 text-xs font-medium",
                        onCredit === c
                          ? "ady-bg-brand text-on-primary"
                          : "text-fg-secondary hover:bg-hover",
                      )}
                    >
                      <Icon name={c ? "file" : "check"} />
                      {c ? t("payments.money.paidLater") : t("payments.money.paidNow")}
                    </button>
                  ))}
                </div>
              </Section>
            ) : null}

            {mode === "expense" && onCredit ? (
              <Section icon="users" title={t("payments.money.supplier")}>
                <Picker
                  options={suppliers}
                  value={supplier}
                  onChange={setSupplier}
                  empty={t("payments.money.noSuppliers")}
                  create={{ kind: "payable", label: t("payments.money.newSupplier") }}
                />
              </Section>
            ) : null}

            {mode === "out" || mode === "pay" || (mode === "expense" && !onCredit) ? (
              <Section icon="upload" title={t("payments.money.paidFrom")}>
                <Picker options={handMoney} value={money} onChange={setMoney} />
              </Section>
            ) : null}

            <Section icon="file" title={t("payments.money.details")}>
              {mode === "bill" ? (
                <IconInput
                  icon="receipt"
                  label={t("payments.money.billNoPh")}
                  value={ref}
                  onChange={setRef}
                  maxLength={40}
                />
              ) : null}
              <IconInput
                icon="pencil"
                label={t("payments.money.notePh")}
                value={note}
                onChange={setNote}
                maxLength={300}
              />
            </Section>
          </div>

          <Side
            onClose={onClose}
            hint={t("payments.money.fixHint")}
            submit={{
              label: t(`payments.money.move.${mode}.save`),
              ready: !missing,
              why: missing,
              loading: post.isPending,
            }}
          >
            <div className="flex flex-col items-center gap-1 rounded-sm border border-card bg-surface px-3 py-3 text-center">
              <span className="text-2xs font-bold tracking-wide text-fg-tertiary uppercase">
                {t(`payments.money.move.${mode}.title`)}
              </span>
              <span
                className={cx(
                  "ady-ledger text-title-sm font-bold",
                  amount ? "text-fg" : "text-fg-tertiary",
                )}
              >
                {formatMoney(amount ?? 0)}
              </span>
              <span className="text-2xs text-fg-tertiary">
                {formatDate(`${date}T12:00:00+05:45`, { style: "medium" })}
              </span>
              <span className="line-clamp-2 w-full text-2xs text-fg-secondary" data-content="user">
                {plan.debit && plan.credit ? memo : t("payments.money.previewHint")}
              </span>
            </div>
            <ul className="flex flex-col gap-1.5">
              {after(plan.credit, "credit")}
              {after(plan.debit, "debit")}
            </ul>
            {negative ? (
              <p className="flex items-start gap-1 text-2xs text-warning-fg" role="status">
                <Icon name="triangle" />
                {t("payments.money.goesNegative")}
              </p>
            ) : null}
          </Side>
        </div>
      )}
    </form>
  );
}

// ---------------------------------------------------------------- account form

export type AccountRole = "money" | "expense" | "income" | "supplier";
const ROLE_KIND: Record<AccountRole, string> = {
  money: "cash",
  expense: "expense",
  income: "revenue",
  supplier: "payable",
};
const ROLE_ICON: Record<AccountRole, IconName> = {
  money: "bank",
  expense: "receipt",
  income: "trending",
  supplier: "users",
};
const MONEY_TYPES = ["cash", "bank", "wallet"] as const;

export function AccountFormPopup({
  role,
  account,
  onClose,
  onSaved,
}: {
  /** null closes the popup. */
  role: AccountRole | null;
  /** Edit this account; omitted adds one. */
  account?: AccountBalance | null;
  onClose: () => void;
  onSaved?: (a: AccountBalance) => void;
}) {
  const title = role ? t(`payments.money.account.${account ? "edit" : "add"}.${role}`) : "";
  return (
    <Popup open={role !== null} onClose={onClose} size="md" label={title}>
      {role ? (
        <AccountForm
          key={account?.id ?? role}
          role={role}
          account={account ?? null}
          title={title}
          onClose={onClose}
          onSaved={onSaved}
        />
      ) : null}
    </Popup>
  );
}

function AccountForm({
  role,
  account,
  title,
  onClose,
  onSaved,
}: {
  role: AccountRole;
  account: AccountBalance | null;
  title: string;
  onClose: () => void;
  onSaved?: (a: AccountBalance) => void;
}) {
  const { orgId, storeId } = useStoreIds();
  const canPost = useCan("finance.post");
  const qc = useQueryClient();
  const toast = useToast();
  const [key] = useState(() => crypto.randomUUID());
  const [name, setName] = useState(account?.name ?? "");
  const [type, setType] = useState<string>(account?.kind ?? ROLE_KIND[role]);
  const [opening, setOpening] = useState<number | null>(null);
  const d = account?.details ?? {};
  const [phone, setPhone] = useState(d.phone ?? "");
  const [pan, setPan] = useState(d.pan ?? "");
  const [address, setAddress] = useState(d.address ?? "");
  const [note, setNote] = useState(d.note ?? "");
  const [error, setError] = useState<string>();
  const details: AccountDetails = {
    phone: phone.trim(),
    pan: pan.trim(),
    address: address.trim(),
    note: note.trim(),
  };
  const save = useMutation({
    mutationFn: () =>
      account
        ? financeApi.updateAccount(
            orgId,
            storeId,
            { id: account.id!, version: account.version ?? 1 },
            { name: name.trim(), ...(role === "supplier" ? { details } : {}) },
          )
        : financeApi.addAccount(
            orgId,
            storeId,
            {
              code: newCode(
                role === "money"
                  ? "acc"
                  : role === "expense"
                    ? "exp"
                    : role === "income"
                      ? "inc"
                      : "sup",
                name,
              ),
              name: name.trim(),
              kind: role === "money" ? type : ROLE_KIND[role],
              ...(opening ? { opening_paisa: opening } : {}),
              ...(role === "supplier" ? { details } : {}),
            },
            key,
          ),
    onSuccess: async (a) => {
      toast.show({
        tone: "success",
        title: t(account ? "payments.money.account.saved" : "payments.money.account.added", {
          name: name.trim(),
        }),
      });
      await qc.invalidateQueries({ queryKey: financeKeys.all(orgId, storeId) });
      onSaved?.(a);
      onClose();
    },
    onError: (e) =>
      setError(
        e instanceof ApiError && e.code === "precondition.version_mismatch"
          ? t("payments.money.account.changed")
          : errorMessage(e),
      ),
  });
  const ready = name.trim() !== "" && canPost;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(undefined);
    if (ready) save.mutate();
  };
  const icon = role === "money" ? (KIND_ICON[type] ?? "bank") : ROLE_ICON[role];
  return (
    <form onSubmit={submit} className="flex flex-col">
      <PopupHead icon={account ? "pencil" : ROLE_ICON[role]} title={title} onClose={onClose} />
      <div className="grid gap-3 pt-3 text-start sm:grid-cols-[minmax(0,1fr)_13rem]">
        <div className="flex min-w-0 flex-col gap-2.5">
          {error ? <Banner kind="error" title={error} /> : null}
          <Section icon={ROLE_ICON[role]} title={t(`payments.money.account.sec.${role}`)}>
            <IconInput
              icon={icon}
              label={t(`payments.money.account.namePh.${role}`)}
              value={name}
              onChange={setName}
              maxLength={80}
            />
            {role === "money" && !account ? (
              <div
                className="grid grid-cols-3 gap-1.5"
                role="group"
                aria-label={t("payments.finance.accountKind")}
              >
                {MONEY_TYPES.map((k) => (
                  <button
                    key={k}
                    type="button"
                    aria-pressed={type === k}
                    onClick={() => setType(k)}
                    className={cx(
                      "flex min-h-control items-center justify-center gap-1.5 rounded-xs border-[1.5px] px-2 text-xs font-medium",
                      type === k
                        ? "ady-outline-brand font-semibold text-link"
                        : "border-card bg-surface text-fg-secondary hover:bg-hover",
                    )}
                  >
                    <Icon name={KIND_ICON[k] ?? "money"} />
                    {t(`payments.finance.kinds.${k}`)}
                  </button>
                ))}
              </div>
            ) : null}
            {role === "expense" && !account ? (
              <div className="flex flex-wrap gap-1">
                {(
                  ["rent", "salary", "packaging", "stock", "ads", "utilities", "transport"] as const
                ).map((x) => {
                  const v = t(`payments.money.account.ideas.${x}`);
                  return (
                    <button
                      key={x}
                      type="button"
                      onClick={() => setName(v)}
                      className="flex min-h-6 items-center gap-1 rounded-full border-[1.5px] border-card bg-surface px-2 text-2xs font-medium text-fg-secondary hover:bg-hover"
                    >
                      <Icon name="plus" />
                      {v}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </Section>

          {role === "supplier" ? (
            <Section icon="phone" title={t("payments.money.account.contact")}>
              <div className="grid gap-1.5 sm:grid-cols-2">
                <IconInput
                  icon="phone"
                  label={t("payments.money.account.phonePh")}
                  value={phone}
                  onChange={setPhone}
                  maxLength={20}
                />
                <IconInput
                  icon="file"
                  label={t("payments.money.account.panPh")}
                  value={pan}
                  onChange={setPan}
                  maxLength={20}
                />
              </div>
              <IconInput
                icon="mapPin"
                label={t("payments.money.account.addressPh")}
                value={address}
                onChange={setAddress}
                maxLength={200}
              />
              <IconInput
                icon="pencil"
                label={t("payments.money.account.notePh")}
                value={note}
                onChange={setNote}
                maxLength={500}
              />
            </Section>
          ) : null}

          {!account && (role === "money" || role === "supplier") ? (
            <Section icon="coins" title={t(`payments.money.account.opening.${role}`)}>
              <MoneyInput
                value={opening}
                onValueChange={(v) => setOpening(v)}
                aria-label={t(`payments.money.account.opening.${role}`)}
              />
              <p className="text-2xs text-fg-tertiary">
                {t(`payments.money.account.openingHelp.${role}`)}
              </p>
            </Section>
          ) : null}
        </div>

        <Side
          onClose={onClose}
          hint={
            account
              ? t("payments.money.account.editHint")
              : t(`payments.money.account.hint.${role}`)
          }
          submit={{
            label: account
              ? t("payments.money.account.save")
              : t(`payments.money.account.add.${role}`),
            ready,
            why: canPost ? t("payments.money.account.needName") : t("errors.forbidden"),
            loading: save.isPending,
          }}
        >
          <div className="flex flex-col items-center gap-1.5 rounded-sm border border-card bg-surface px-3 py-3 text-center">
            <span className="flex size-9 items-center justify-center rounded-full bg-selected text-link">
              <Icon name={icon} />
            </span>
            <span className="w-full truncate text-sm font-semibold" data-content="user">
              {name.trim() || t(`payments.money.account.role.${role}`)}
            </span>
            <span className="w-full truncate text-2xs text-fg-tertiary">
              {role === "supplier"
                ? [
                    phone.trim() && /^\+?\d{7,15}$/.test(phone.trim())
                      ? formatPhone(phone.trim())
                      : phone.trim(),
                    pan.trim() && `PAN ${pan.trim()}`,
                  ]
                    .filter(Boolean)
                    .join(" · ") || t("payments.money.account.contactHint")
                : role === "money"
                  ? t(`payments.finance.kinds.${type as "cash"}`)
                  : t(`payments.money.account.role.${role}`)}
            </span>
            {account ? (
              <span className="ady-ledger text-xs font-semibold">
                <Money paisa={account.balance_paisa} />
              </span>
            ) : opening ? (
              <span className="ady-ledger text-xs font-semibold">{formatMoney(opening)}</span>
            ) : null}
          </div>
        </Side>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------- entry detail

export function KindPill({ kind }: { kind: EntryKind }) {
  return (
    <span
      className={cx(
        "inline-flex min-h-5 items-center gap-1 rounded-full px-2 text-2xs font-bold whitespace-nowrap uppercase",
        ENTRY_KIND_TONE[kind],
      )}
    >
      <Icon name={ENTRY_KIND_ICON[kind]} />
      {t(`payments.money.kinds.${kind}`)}
    </span>
  );
}

export function EntryPopup({
  entry,
  reversed,
  onClose,
}: {
  entry: JournalEntry | null;
  /** This entry already has a reversal. */
  reversed: boolean;
  onClose: () => void;
}) {
  return (
    <Popup open={entry !== null} onClose={onClose} size="md" label={entry?.memo ?? ""}>
      {entry ? (
        <EntryDetail key={entry.id} entry={entry} reversed={reversed} onClose={onClose} />
      ) : null}
    </Popup>
  );
}

function EntryDetail({
  entry,
  reversed,
  onClose,
}: {
  entry: JournalEntry;
  reversed: boolean;
  onClose: () => void;
}) {
  const { orgId, storeId } = useStoreIds();
  const canPost = useCan("finance.post");
  const qc = useQueryClient();
  const toast = useToast();
  const stepUp = useStepUp();
  const accounts = useAccounts();
  const list = accounts.data?.data ?? [];
  const byCode = new Map(list.map((a) => [a.code, a]));
  const kind = entryKind(entry, (c) => byCode.get(c)?.kind);
  const [confirming, setConfirming] = useState(false);
  const [key] = useState(() => crypto.randomUUID());
  const reverse = useMutation({
    mutationFn: () => stepUp(() => financeApi.reverse(orgId, storeId, entry.id, key)),
    onSuccess: async () => {
      toast.show({ tone: "success", title: t("payments.finance.reversed") });
      await qc.invalidateQueries({ queryKey: financeKeys.all(orgId, storeId) });
      onClose();
    },
    onError: (e) => toast.show({ tone: "error", title: stepUpErrorMessage(e) ?? errorMessage(e) }),
  });
  const total = entryTotal(entry);
  const canReverse = !entry.reversal_of && !reversed;
  return (
    <div className="flex flex-col">
      <PopupHead
        icon={ENTRY_KIND_ICON[kind]}
        title={t("payments.money.entry.title")}
        onClose={onClose}
      />
      <div className="grid gap-3 pt-3 text-start sm:grid-cols-[minmax(0,1fr)_13rem]">
        <div className="flex min-w-0 flex-col gap-2.5">
          {reversed ? <Banner kind="info" title={t("payments.money.entry.isReversed")} /> : null}
          {entry.reversal_of ? (
            <Banner kind="info" title={t("payments.money.entry.isReversal")} />
          ) : null}
          <Section icon="list" title={t("payments.money.entry.lines")}>
            <table className="w-full text-xs">
              <caption className="ady-sr-only">{t("payments.money.entry.lines")}</caption>
              <thead>
                <tr className="border-b border-subtle text-fg-tertiary">
                  <th scope="col" className="py-1.5 text-start font-semibold">
                    {t("payments.finance.columns.account")}
                  </th>
                  <th scope="col" className="py-1.5 text-end font-semibold">
                    {t("payments.money.entry.debit")}
                  </th>
                  <th scope="col" className="py-1.5 text-end font-semibold">
                    {t("payments.money.entry.credit")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {entry.lines.map((l, i) => {
                  const a = byCode.get(l.account);
                  return (
                    <tr key={i} className="border-b border-subtle last:border-0">
                      <td className="py-1.5">
                        <span className="flex items-center gap-1.5">
                          <span className="text-fg-tertiary">
                            <Icon name={KIND_ICON[a?.kind ?? ""] ?? "money"} />
                          </span>
                          <span className="truncate" data-content="user">
                            {a ? nameOf(a) : l.account}
                          </span>
                          {l.dimension ? (
                            <span className="text-2xs text-fg-tertiary">· {l.dimension}</span>
                          ) : null}
                        </span>
                      </td>
                      <td className="ady-ledger py-1.5 text-end">
                        {l.debit_paisa ? formatMoney(l.debit_paisa) : ""}
                      </td>
                      <td className="ady-ledger py-1.5 text-end">
                        {l.credit_paisa ? formatMoney(l.credit_paisa) : ""}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="text-2xs text-fg-tertiary">{t("payments.money.entry.linesHelp")}</p>
          </Section>
          {canReverse && confirming ? (
            <div
              role="alert"
              className="flex flex-col gap-1.5 rounded-sm border border-warning-border bg-warning-bg px-2.5 py-2"
            >
              <span className="flex items-center gap-1 text-xs font-semibold text-warning-fg">
                <Icon name="triangle" />
                {t("payments.money.entry.confirmTitle")}
              </span>
              <span className="text-2xs text-fg-secondary">
                {t("payments.money.entry.confirmBody", { amount: formatMoney(total) })}
              </span>
            </div>
          ) : null}
        </div>
        <div className="flex flex-col gap-2.5 rounded-sm border border-card bg-sunken p-3">
          <div className="flex flex-col items-center gap-1 rounded-sm border border-card bg-surface px-3 py-3 text-center">
            <KindPill kind={kind} />
            <span className="ady-ledger text-title-sm font-bold">{formatMoney(total)}</span>
            <span className="line-clamp-3 w-full text-xs font-medium" data-content="user">
              {entry.memo}
            </span>
            <span className="text-2xs text-fg-tertiary">{formatDateTime(entry.occurred_at)}</span>
          </div>
          <p className="text-2xs text-fg-tertiary">
            {t(
              entry.source_type === "manual"
                ? "payments.money.entry.byHand"
                : "payments.money.entry.automatic",
            )}
          </p>
          <div className="mt-auto flex flex-col-reverse gap-1.5 pt-1">
            <Button
              variant="secondary"
              fullWidth
              onClick={confirming ? () => setConfirming(false) : onClose}
            >
              {confirming ? t("payments.money.cancel") : t("payments.money.close")}
            </Button>
            {canReverse ? (
              <Button
                variant={confirming ? "danger" : "secondary"}
                icon="undo"
                fullWidth
                disabled={!canPost}
                title={canPost ? undefined : t("errors.forbidden")}
                className="disabled:cursor-not-allowed disabled:opacity-45"
                loading={reverse.isPending}
                onClick={() => (confirming ? reverse.mutate() : setConfirming(true))}
              >
                {confirming ? t("payments.money.entry.confirm") : t("payments.finance.reverse")}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- account detail

export function AccountPopup({
  account,
  onClose,
  onMove,
  onEdit,
}: {
  account: AccountBalance | null;
  onClose: () => void;
  /** Opens a money popup preset for this account. */
  onMove: (mode: MoveMode, preset: MovePreset) => void;
  onEdit?: (a: AccountBalance) => void;
}) {
  return (
    <Popup
      open={account !== null}
      onClose={onClose}
      size="md"
      label={account ? nameOf(account) : ""}
    >
      {account ? (
        <AccountDetail
          key={account.id ?? account.code}
          account={account}
          onClose={onClose}
          onMove={onMove}
          onEdit={onEdit}
        />
      ) : null}
    </Popup>
  );
}

function AccountDetail({
  account: a,
  onClose,
  onMove,
  onEdit,
}: {
  account: AccountBalance;
  onClose: () => void;
  onMove: (mode: MoveMode, preset: MovePreset) => void;
  onEdit?: (a: AccountBalance) => void;
}) {
  const { orgId, storeId } = useStoreIds();
  const entries = useQuery({
    queryKey: financeKeys.entries(orgId, storeId, a.id ?? a.code),
    queryFn: () => financeApi.accountEntries(orgId, storeId, a.id!),
    enabled: a.id !== null,
  });
  const k = a.id ?? a.code;
  const rows = entries.data?.data ?? [];
  const moneyIn = rows.filter((r) => r.change_paisa > 0).reduce((x, r) => x + r.change_paisa, 0);
  const moneyOut = rows.filter((r) => r.change_paisa < 0).reduce((x, r) => x - r.change_paisa, 0);
  const supplier = isSupplier(a);
  const d = a.details ?? {};
  const actions: { icon: IconName; label: string; run: () => void }[] = isHandMoney(a)
    ? [
        {
          icon: "download",
          label: t("payments.finance.actions.moneyIn"),
          run: () => onMove("in", { money: k }),
        },
        {
          icon: "upload",
          label: t("payments.finance.actions.moneyOut"),
          run: () => onMove("out", { money: k }),
        },
        {
          icon: "swap",
          label: t("payments.finance.actions.transfer"),
          run: () => onMove("transfer", { money: k }),
        },
      ]
    : supplier
      ? [
          {
            icon: "file",
            label: t("payments.money.move.bill.title"),
            run: () => onMove("bill", { other: k }),
          },
          {
            icon: "users",
            label: t("payments.money.move.pay.title"),
            run: () => onMove("pay", { other: k }),
          },
        ]
      : isExpense(a)
        ? [
            {
              icon: "receipt",
              label: t("payments.finance.actions.addExpense"),
              run: () => onMove("expense", { other: k }),
            },
          ]
        : [];
  return (
    <div className="flex flex-col">
      <PopupHead icon={KIND_ICON[a.kind] ?? "money"} title={nameOf(a)} onClose={onClose} />
      <div className="flex flex-col gap-2.5 pt-3 text-start">
        <div className="grid gap-1.5 sm:grid-cols-3">
          {(
            [
              [
                supplier ? t("payments.money.youOwe") : t("payments.finance.columns.balance"),
                a.balance_paisa,
              ],
              [supplier ? t("payments.money.billed") : t("payments.finance.kpis.moneyIn"), moneyIn],
              [supplier ? t("payments.money.paid") : t("payments.finance.kpis.moneyOut"), moneyOut],
            ] as const
          ).map(([label, v]) => (
            <div
              key={label}
              className="flex min-w-0 flex-col gap-0.5 rounded-sm border border-card bg-surface px-2.5 py-2"
            >
              <span className="text-2xs font-semibold text-fg-tertiary">{label}</span>
              <span className="ady-ledger truncate text-base font-semibold">{formatMoney(v)}</span>
            </div>
          ))}
        </div>
        {a.opening_paisa ? (
          <p className="-mt-1 text-2xs text-fg-tertiary">
            {t(supplier ? "payments.money.openingOwed" : "payments.money.openingIncluded", {
              amount: formatMoney(a.opening_paisa),
            })}
          </p>
        ) : null}
        {supplier && (d.phone || d.pan || d.address || d.note) ? (
          <dl className="grid gap-x-4 gap-y-1 rounded-sm border border-card bg-sunken px-3 py-2 text-xs sm:grid-cols-2">
            {d.phone ? (
              <div className="flex gap-2">
                <dt className="font-semibold text-fg-tertiary">
                  {t("payments.money.account.phone")}
                </dt>
                <dd>
                  <a href={`tel:${d.phone}`} className="text-link tabular-nums">
                    {/^\+?\d{7,15}$/.test(d.phone) ? formatPhone(d.phone) : d.phone}
                  </a>
                </dd>
              </div>
            ) : null}
            {d.pan ? (
              <div className="flex gap-2">
                <dt className="font-semibold text-fg-tertiary">
                  {t("payments.money.account.pan")}
                </dt>
                <dd className="tabular-nums">{d.pan}</dd>
              </div>
            ) : null}
            {d.address ? (
              <div className="flex gap-2 sm:col-span-2">
                <dt className="font-semibold text-fg-tertiary">
                  {t("payments.money.account.address")}
                </dt>
                <dd data-content="user">{d.address}</dd>
              </div>
            ) : null}
            {d.note ? (
              <div className="flex gap-2 sm:col-span-2">
                <dt className="font-semibold text-fg-tertiary">
                  {t("payments.money.account.note")}
                </dt>
                <dd data-content="user">{d.note}</dd>
              </div>
            ) : null}
          </dl>
        ) : null}
        {actions.length || (onEdit && !a.system) ? (
          <div className="flex flex-wrap gap-1.5">
            {actions.map((x) => (
              <Button key={x.label} size="sm" variant="secondary" icon={x.icon} onClick={x.run}>
                {x.label}
              </Button>
            ))}
            {onEdit && !a.system ? (
              <Button size="sm" variant="ghost" icon="pencil" onClick={() => onEdit(a)}>
                {t("payments.money.edit")}
              </Button>
            ) : null}
          </div>
        ) : null}
        <Section icon="list" title={t("payments.money.history")}>
          {a.id === null ? (
            <p className="py-4 text-center text-xs text-fg-tertiary">
              {t("payments.money.noHistory")}
            </p>
          ) : entries.isPending ? (
            <SkeletonText lines={4} />
          ) : entries.isError ? (
            <p className="text-xs text-danger-fg">{errorMessage(entries.error)}</p>
          ) : rows.length === 0 ? (
            <p className="py-4 text-center text-xs text-fg-tertiary">
              {t("payments.money.noHistory")}
            </p>
          ) : (
            <ul className="flex max-h-72 flex-col divide-y divide-subtle overflow-y-auto rounded-sm border border-card bg-surface">
              {rows.map((r) => (
                <li
                  key={`${r.entry_id}-${r.debit_paisa}-${r.credit_paisa}`}
                  className="flex items-center gap-2 px-2.5 py-1.5"
                >
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-xs font-medium" data-content="user">
                      {r.memo}
                    </span>
                    <span className="text-2xs text-fg-tertiary">
                      {formatDateTime(r.occurred_at)}
                      {r.reversal ? ` · ${t("payments.finance.reversal")}` : ""}
                    </span>
                  </span>
                  <span
                    className={cx(
                      "ady-ledger text-xs font-semibold whitespace-nowrap",
                      r.change_paisa > 0
                        ? supplier
                          ? "text-warning-fg"
                          : "text-success-fg"
                        : supplier
                          ? "text-success-fg"
                          : "text-danger-fg",
                    )}
                  >
                    {r.change_paisa > 0 ? "+" : "−"}
                    {formatMoney(Math.abs(r.change_paisa))}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </div>
  );
}

/** Accounts grouped by role, for the pages (memoised over the accounts query). */
export function useAccountGroups() {
  const q = useAccounts();
  const groups = useMemo(() => {
    const list = q.data?.data ?? [];
    return {
      list,
      money: list.filter(isMoney),
      expenses: list.filter(isExpense),
      suppliers: list.filter(isSupplier),
      byCode: new Map(list.map((a) => [a.code, a])),
    };
  }, [q.data]);
  return { q, ...groups };
}
